import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { groupTripApi } from '../../api/groupTripApi';
import { travelVipApi } from '../../api/travelVipApi';
import { groupTripErrorMessage, normalizeGroupTripList, occupancyCopy } from './groupTripModel';
import { groupTripRecruitmentStatusLabel } from './groupTripPresentation';
import HostCommissionDisclosure from './HostCommissionDisclosure';
import './group-trip.css';

export default function HostGroupTripsPage() {
  const { t, tf } = useTravelTranslation();
  const [items, setItems] = useState([]);
  const [entitlements, setEntitlements] = useState(null);
  const [vipLoadFailed, setVipLoadFailed] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const generation = useRef(0);

  const load = useCallback(async () => {
    const token = ++generation.current;
    setPhase('loading');
    setError('');
    setVipLoadFailed(false);
    try {
      const [page, vipResult] = await Promise.all([
        groupTripApi.listMine({ limit: 50 }).then((raw) => normalizeGroupTripList(raw, t)),
        travelVipApi.getMyEntitlements().then((vip) => ({ ok: true, vip })).catch(() => ({ ok: false, vip: null })),
      ]);
      if (token !== generation.current) return;
      setItems(page.items);
      setEntitlements(vipResult.vip);
      setVipLoadFailed(!vipResult.ok);
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      setError(groupTripErrorMessage(cause, t));
      setPhase('error');
    }
  }, [t]);

  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);

  const vipBadge = entitlements?.hostVipBadgeEnabled ? t('travel.groupTrip.hostVipBadge') : null;
  const quotaSummary = entitlements
    ? tf('travel.groupTrip.hostQuotaSummary', {
      active: entitlements.activeHostedGroupTrips,
      max: entitlements.maxActiveHostedGroupTrips,
    })
    : null;

  return (
    <main className="group-trips" aria-labelledby="group-trips-title">
      <header className="group-trips__intro">
        <p>{t('travel.common.travel')}</p>
        <h1 id="group-trips-title">{t('travel.groupTrip.myGroupTrips')}</h1>
        {vipBadge ? <span className="group-trips__vip-badge">{vipBadge}</span> : null}
        {quotaSummary ? <span>{quotaSummary}</span> : null}
        <span>{t('travel.groupTrip.createCapacityHint')}</span>
        {/* PD-GT-02: navigate to picker first; bare /group-trips/new requires publicationId */}
        <Link className="group-trips__link" to="/group-trips/picker">{t('travel.groupTrip.createTitle')}</Link>
        <Link className="group-trips__link" to="/host/earnings">{t('travel.nav.earnings')}</Link>
        <Link className="group-trips__link" to="/public/group-trips">{t('travel.groupTrip.publicRecruitment')}</Link>
      </header>
      {phase === 'ready' || vipLoadFailed ? (
        <HostCommissionDisclosure
          loadFailed={vipLoadFailed && !entitlements}
          feeEnabled={Boolean(entitlements?.feeEnabled)}
          vip={Boolean(entitlements?.vip)}
          platformFeeRate={entitlements?.platformFeeRate ?? 0}
          includedInDisplayPrice={entitlements?.includedInDisplayPrice !== false}
        />
      ) : null}
      {phase === 'loading' ? <section className="group-trips__state"><p>{t('travel.common.loading')}</p></section> : null}
      {phase === 'error' ? (
        <section className="group-trips__state group-trips__state--error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={load}>{t('travel.common.retry')}</button>
        </section>
      ) : null}
      {phase === 'ready' && items.length === 0 ? (
        <section className="group-trips__state"><p>{t('travel.groupTrip.hostEmptyList')}</p></section>
      ) : null}
      {items.length ? (
        <ul className="group-trips__list">
          {items.map((item) => (
            <li className="group-trips__card" key={item.groupTripId}>
              <Link to={`/group-trips/${item.groupTripId}`}>
                <h2>{item.recruitmentTitle}</h2>
                <p>{groupTripRecruitmentStatusLabel(item.recruitmentStatus)}</p>
                <p className="group-trips__hint">{occupancyCopy(item, t, tf)}</p>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </main>
  );
}
